import { useCallback, useEffect, useState } from 'react'
import { UseFollowIconArgs, UseFollowIconReturn } from './types'
import type { CSSProperties } from 'react'

const OFFSET_DEFAULT = 5

export const useFollowIcon = ({
    parentRef,
    targets,
    isAnimating,
    layoutKey,
}: UseFollowIconArgs): UseFollowIconReturn => {
    const [translateY, setTranslateY] = useState<number | null>(null)

    const moveIcon = useCallback(() => {
        // スライダー遷移中は計算しない。isAnimatingがfalseになると再実行される
        if (isAnimating) return

        const parentEl = parentRef.current
        // isComplete が false の最初の要素を探す
        // 全部 complete の場合は最後の要素（ボタン）にとどまる
        const currentTarget = targets.find(t => !t.isComplete) ?? targets[targets.length - 1]
        const targetEl = currentTarget?.ref.current

        if (!parentEl || !targetEl) {
            setTranslateY(null)
            return
        }

        const parentRect = parentEl.getBoundingClientRect()
        const targetRect = targetEl.getBoundingClientRect()

        // ターゲットごとのoffsetを使用（未指定時はOFFSET_DEFAULT）
        const offset = currentTarget.offset ?? OFFSET_DEFAULT

        // 親要素からの相対距離に変換して translateY にセット
        setTranslateY(targetRect.top - parentRect.top + offset)
    }, [parentRef, targets, isAnimating, layoutKey])

    // バリデーション状態変化 or layoutKey変化時に移動（DOM更新後に計算するため requestAnimationFrame を使用）
    useEffect(() => {
        const id = requestAnimationFrame(moveIcon)
        return () => cancelAnimationFrame(id)
    }, [moveIcon])

    // レイアウト変化（エラーメッセージ表示・リサイズ等）への追従
    useEffect(() => {
        const parentEl = parentRef.current
        if (!parentEl) return

        const handle = () => requestAnimationFrame(moveIcon)
        window.addEventListener('resize', handle)

        let observer: ResizeObserver | undefined
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(handle)
            observer.observe(parentEl)
        }

        return () => {
            window.removeEventListener('resize', handle)
            observer?.disconnect()
        }
    }, [parentRef, moveIcon])

    const iconStyle: CSSProperties =
        translateY !== null ? { transform: `translateY(${translateY}px)` } : {}

    return { iconStyle }
}
